import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export function useDashboardStats() {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["dashboard-stats", activeOrgId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      const [unitsRes, hostsRes, bookingsRes, configRes] = await Promise.all([
        supabase.from("units").select("*").eq("organization_id", activeOrgId!),
        supabase.from("hosts").select("*").eq("organization_id", activeOrgId!),
        supabase.from("bookings").select("*").eq("organization_id", activeOrgId!),
        supabase.from("strata_config").select("*").eq("organization_id", activeOrgId!).single(),
      ]);

      const units = unitsRes.data ?? [];
      const hosts = hostsRes.data ?? [];
      const bookings = bookingsRes.data ?? [];
      const config = configRes.data;

      const totalUnits = units.length;
      const strApproved = units.filter((u: any) => u.str_approved).length;
      const activeHosts = hosts.filter((h: any) => h.status === "approved").length;
      const totalBookings = bookings.length;
      const approvedBookings = bookings.filter((b: any) => b.status === "approved").length;
      const blockedBookings = bookings.filter((b: any) => b.status === "blocked").length;
      const pendingBookings = bookings.filter((b: any) => b.status === "pending").length;
      const completedBookings = bookings.filter((b: any) => b.status === "completed").length;

      // Compliance rate: approved / (approved + blocked) * 100
      const validatedBookings = approvedBookings + blockedBookings;
      const complianceRate = validatedBookings > 0
        ? ((approvedBookings / validatedBookings) * 100).toFixed(1)
        : "100.0";

      // Night usage per unit
      const nightUsage = hosts.map((h: any) => {
        const unit = units.find((u: any) => u.id === h.unit_id);
        return {
          unit: unit?.unit_number ?? "?",
          used: h.nights_used ?? 0,
          limit: h.night_limit ?? 90,
          percent: h.night_limit > 0 ? Math.round(((h.nights_used ?? 0) / h.night_limit) * 100) : 0,
          hostName: h.profile?.full_name ?? "Unknown",
        };
      });

      return {
        totalUnits,
        strApproved,
        activeHosts,
        totalBookings,
        approvedBookings,
        blockedBookings,
        pendingBookings,
        completedBookings,
        complianceRate,
        nightUsage,
        config,
        units,
        hosts,
        bookings,
      };
    },
  });
}

/** For PM portfolio view: fetch summary stats for a specific org */
export function useOrgStats(orgId: string | null) {
  return useQuery({
    queryKey: ["org-stats", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const [unitsRes, hostsRes, bookingsRes] = await Promise.all([
        supabase.from("units").select("id, status, str_approved").eq("organization_id", orgId!),
        supabase.from("hosts").select("id, status").eq("organization_id", orgId!),
        supabase.from("bookings").select("id, status").eq("organization_id", orgId!),
      ]);

      const units = unitsRes.data ?? [];
      const hosts = hostsRes.data ?? [];
      const bookings = bookingsRes.data ?? [];

      return {
        orgId,
        totalUnits: units.length,
        activeHosts: hosts.filter((h: any) => h.status === "approved").length,
        pendingHosts: hosts.filter((h: any) => h.status === "pending").length,
        activeBookings: bookings.filter((b: any) => b.status === "approved").length,
        pendingBookings: bookings.filter((b: any) => b.status === "pending").length,
        complianceAlerts: hosts.filter((h: any) => h.status === "suspended" || h.status === "expired").length,
      };
    },
  });
}
